<template>
  <div id="co-table-container">
    <div class="co-header-container">
      <div class="co-header">
        Orders
      </div>
      <div class="grid-column ai-c">
        <SACTableSearch
          :search="coTableSearch"
          @input="(value) => changeSearch(value.trim())"
        />
        <SACSettingMenu
          v-if="isSmacAdmin"
          :options="menuOptions"
        />
      </div>
    </div>
    <div class="co-container">
      <v-data-table
        :headers="headers"
        :items="customerOrders"
        :loading="loadingOrders"
        :options="coTableOptions"
        :search="coTableSearch"
        height="700px"
        fixed-header
        hide-default-footer
      >
        <template #[`item.orderNumber`]="{ item }">
          <td>
            <div class="o-status-container">
              <span
                v-if="determineOrderStatusDot(item) !== 'error'
                  && determineOrderStatusDot(item) !== 'cancelled'"
                :class="[determineOrderStatusDot(item), 'dot']"
              />
              <span
                v-else-if="determineOrderStatusDot(item) === 'error'"
                class="error-status"
              >
                !
              </span>
              <span
                v-else-if="determineOrderStatusDot(item) === 'cancelled'"
                class="cancelled-status"
              >
                -
              </span>
              <span v-if="isSmacAdmin">
                <router-link
                  :to="`/order/customer/${item.orderNumber}`"
                >
                  {{ item.orderNumber }}
                </router-link>
              </span>
              <span v-else>
                {{ item.orderNumber }}
              </span>
            </div>
          </td>
        </template>
        <template #[`item.status`]="{ item }">
          <td>
            <span :class="[determineOrderStatus(item.status)]">
              {{ item.status }}
            </span>
          </td>
        </template>
        <template #[`item.orderDate`]="{ item }">
          <td>
            {{ customDateFilter(item.orderDate, dateFormat) }}
          </td>
        </template>
        <template #[`item.shipDate`]="{ item }">
          <td>
            {{ customDateFilter(item.shipDate, dateFormat) }}
          </td>
        </template>
        <template #[`item.deliveryDate`]="{ item }">
          <td>
            {{ customDateFilter(item.deliveryDate, dateFormat) }}
          </td>
        </template>
        <template #[`item.trackingNumber`]="{ item }">
          <td>
            <a
              v-if="item.trackingNumber"
              :href="createTrackingLink(item)"
              target="_blank"
            >
              {{ item.trackingNumber }}
            </a>
            <span v-else>
              --
            </span>
          </td>
        </template>
        <template #[`item.campaignCode`]="{ item }">
          <td>
            {{ item.campaignCode || "--" }}
          </td>
        </template>
      </v-data-table>
    </div>
    <SACTableFooter
      :totalRecords="customerOrders?.length"
      :rowCount="coTableOptions.itemsPerPage"
      :changeRowCount="changeRowCount"
      :page="coTableOptions.page"
      :totalPage="coTableRecordCount"
      :changePage="changePage"
      :loading="loadingOrders"
    />
  </div>
</template>

<script>
import SACTableSearch from '@shared/components/table/SACTableSearch.vue';
import SACTableFooter from '@shared/components/table/SACTableFooter.vue';
import SACSettingMenu from '@shared/components/sac/SACSettingMenu.vue';

import customerDetailMixin from '@shared/mixins/customerDetailMixin';
import unitPreferencesMixin from '@shared/mixins/unitPreferencesMixin';
import orderStatusMixin from '@shared/mixins/orderStatusMixin';

export default {
  name: 'CustomerOrderTable',
  components: {
    SACTableSearch,
    SACTableFooter,
    SACSettingMenu,
  },
  mixins: [customerDetailMixin, unitPreferencesMixin, orderStatusMixin],
  data() {
    return {
      menuOptions: [
        {
          label: 'Place an Order',
          action: () => this.toggleOrderActionModal(true, 'new-order'),
          class: 'common-btn',
        },
      ],
      headers: [{
        text: 'Order Number',
        align: 'start',
        sortable: true,
        filterable: true,
        value: 'orderNumber',
      }, {
        text: 'Order Status',
        align: 'start',
        sortable: true,
        filterable: true,
        value: 'status',
      }, {
        text: 'Order Date',
        align: 'start',
        sortable: true,
        filterable: true,
        value: 'orderDate',
      }, {
        text: 'Ship Date',
        align: 'start',
        sortable: true,
        filterable: true,
        value: 'shipDate',
      }, {
        text: 'Delivery Date',
        align: 'start',
        sortable: true,
        filterable: true,
        value: 'deliveryDate',
      }, {
        text: 'Tracking Number',
        align: 'start',
        sortable: true,
        filterable: true,
        value: 'trackingNumber',
      }, {
        text: 'Campaign',
        align: 'start',
        sortable: true,
        filterable: true,
        value: 'campaignCode',
      }],
    };
  },
  mounted() {
    this.$store.dispatch('getAllCustomerOrdersById', { customerId: this.$route.params.customerId });
  },
  methods: {
    changePage(page) {
      this.$store.dispatch('changeCoTableOption', {
        field: 'page',
        value: page,
      });
    },
    changeRowCount(rowCount) {
      this.$store.dispatch('changeCoTableOption', {
        field: 'itemsPerPage',
        value: rowCount,
      });
    },
    changeSort(item) {
      this.$store.dispatch('changeCoTableOption', {
        field: 'sortBy',
        value: [item],
      });
    },
    changeSearch(value) {
      this.$store.dispatch('changeCustomerDetailValue', {
        field: 'coTableSearch',
        value,
      });
    },
  },
};
</script>

<style lang="scss">

#co-table-container {
  width: 100%;
  max-width: 1705px;
  padding-top: 20px;
  .co-header-container {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 18px;
    .co-header {
      font-size: 1.5625rem;
      color: $N100;
      font-weight: 700;
    }
  }
  .co-container {
    background-color: $WHITE;
    box-shadow: 0 0 7px 2px rgba(9, 15, 32, 0.02);
    border-radius: 6px;
    padding-bottom: 12px;
    .v-data-table__wrapper {
      height: 700px;

      thead {
        tr {
          th {
            background-color: $N100;
            color: $WHITE;
            font-size: 15px;
            font-weight: 500;
            text-transform: uppercase;

            .mdi-checkbox-blank-outline::before,
            .mdi-checkbox-marked::before,
            .mdi-minus-box::before,
            .mdi-arrow-up::before {
              color: $WHITE;
            }
            &:first-child {
              border-top-left-radius: 6px;
            }
            &:last-child {
              border-top-right-radius: 6px;
            }
            &.sortable {
              i {
                display: none;
              }
            }
            &.active {
              i {
                display: inline-block;
              }
            }
          }
        }
      }

      tbody {
        tr {
          td {
            border-bottom: 0;

            .mdi-checkbox-marked::before {
              color: $NBLUE100;
            }
            .edit-btn {
              background: $N90;
              color: $WHITE;
              width: 70px;
              padding: 3px;
            }
          }

          &:hover {
            background: none;
          }

          &:nth-child(even) {
            background-color: $NBLUE5;
          }
        }
      }
    }
  }
  .o-status-container {
    display: flex;
    align-items: center;
  }
  .green-dot {
    background-color: $BASIL100;
    width: 8px;
    height: 8px;
    border-radius: 4px;
    margin-right: 12px;
  }
  .red-dot {
    background-color: $TOMATO100;
    width: 8px;
    height: 8px;
    border-radius: 4px;
    margin-right: 12px;
  }
  .yellow-dot {
    background-color: $SUNSET100;
    width: 8px;
    height: 8px;
    border-radius: 4px;
    margin-right: 12px;
  }
  .error-status {
    width: 8px;
    margin-right: 12px;
    text-align: center;
    color: $TOMATO100;
    font-weight: 700;
    font-size: 1rem;
    transform: scale(1.2);
  }
  .cancelled-status {
    width: 8px;
    margin-right: 12px;
    text-align: center;
    color: $BLACK;
    font-weight: 700;
    font-size: 1rem;
    transform: scale(1.2);
  }
}
</style>
